import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'
import type { SetURLSearchParams } from 'react-router-dom'
import { hasActiveSubscription } from 'Model/User/IUser.js'
import type { IUser } from 'Model/User/IUser.js'
import type { CreditBalanceDTO } from '../../../Generated/CreditBalanceDTO.ts'
import { trackBilling } from '../../../Services/Telemetry/index.js'

interface UseCheckoutReturnOptions {
  user: IUser | undefined
  balance: CreditBalanceDTO | null
  isCheckoutReturn: boolean
  isRechargeReturn: boolean
  setSearchParams: SetURLSearchParams
  reload: () => void
  setAwaitingSync: (value: boolean) => void
  setRechargePending: (value: boolean) => void
  rechargeInitialBalance: RefObject<number | null>
}

export function useCheckoutReturn({
  user,
  balance,
  isCheckoutReturn,
  isRechargeReturn,
  setSearchParams,
  reload,
  setAwaitingSync,
  setRechargePending,
  rechargeInitialBalance
}: UseCheckoutReturnOptions): void {
  const checkoutHandled = useRef(false)

  useEffect(() => {
    if (checkoutHandled.current) return
    if (!isCheckoutReturn && !isRechargeReturn) return
    checkoutHandled.current = true
    setSearchParams({}, { replace: true })

    if (isRechargeReturn) {
      trackBilling('recharge_success')
      rechargeInitialBalance.current = balance?.balance ?? null
      setRechargePending(true)
      reload()
      return
    }

    trackBilling('checkout_success')

    // wait for webhook to sync to Keycloak
    if (user && !hasActiveSubscription(user.org)) {
      setAwaitingSync(true)
    }
  }, [isCheckoutReturn, isRechargeReturn, setSearchParams, reload, user, balance])
}
